import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketRootCause } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-root-causes-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './root-causes-form.component.html'
})
export class TicketsRootCausesFormComponent implements OnInit, OnDestroy {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TicketRootCause>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';
  parentOptions: TicketRootCause[] = [];

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: [''],
    idParent: [null],
    sortOrder: [0],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.cargarParents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarParents(): void {
    this.configApi
      .getRootCauses()
      .pipe(catchError(() => of([] as TicketRootCause[])), takeUntil(this.destroy$))
      .subscribe((items) => {
        this.parentOptions = items.sort((a, b) => a.name.localeCompare(b.name));
      });
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({ name: '', description: '', idParent: null, sortOrder: 0, isActive: true });
  }

  loadRootCause(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getRootCauseById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          idParent: item.idParent,
          sortOrder: item.sortOrder ?? 0,
          isActive: item.isActive
        });
        this.originalName = item.name;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la causa raíz';
      }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;

    if (this.isEditing && this.itemId != null && raw.idParent === this.itemId) {
      this.saving = false;
      this.error = 'Una causa raíz no puede ser su propio padre';
      return;
    }

    const req = {
      name: raw.name,
      description: raw.description || undefined,
      idParent: raw.idParent ?? undefined,
      sortOrder: Number(raw.sortOrder ?? 0),
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateRootCause(this.itemId, req)
      : this.configApi.createRootCause(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Causa raíz',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Causa raíz', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la causa raíz';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }
}
